/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: cognito,認証,idトークン,ログイン,ログアウト
 * note:
 *  - Cognitoへのログイン,ログアウトの機能を提供する
 *  - 取得したIDトークン内の情報を解析してアプリに提供する(getEmailなど)
 * sample:
 *  - 全てのプロジェクト
 * :ZNOTE:
 */

import {AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserAttribute, CognitoUserPool, ICognitoUserData, ICognitoUserPoolData} from 'amazon-cognito-identity-js';
import {parseJwt} from '@zettant/commonUtils';

export interface ILoginResult { success: boolean, message: any, code: number, result: any }

export const returnCodes = {
  http: {
    success: { code: 200, msg: '' },
    notFound: { code: 404, msg: '' }
  },
  general: {
    success: { code: 0, msg: 'OK' },
    invalidCmd: { code: 10, msg: '不正なコマンドです' }
  },
  auth: {
    loginFailed: { code: 20, msg: 'ログインに失敗しました' },
    invalidToken: { code: 21, msg: '不正なユーザからのリクエストです' },
    passwordRestRequired: { code: 22, msg: 'パスワードの再設定が必要です' },
    passwordForceChangeRequired: { code: 23, msg: '初期パスワードの変更が必要です' },
    verifyRequired: { code: 24, msg: '検証コードによる確認が必要です' },
    resetPasswordProcessFailed: { code: 25, msg: 'パスワードリセットに失敗しました' },
    forgotPasswordProcessFailed: { code: 26, msg: 'パスワード再設定プロセスの起動に失敗しました' }
  }
};

let awsResources: any;
let poolName: string;
const poolData: ICognitoUserPoolData = {
  UserPoolId: '',
  ClientId: ''
};

export const getPoolData = () => poolData;


export const setLastLoginUser = () => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('lastLoginUser', getEmail());
  }
};
export const isDifferentUser = () => localStorage.getItem('lastLoginUser') !== getEmail();

export const setAwsParams = (resource: any, userpoolName) => {
  awsResources = Object.assign({}, resource);
  poolName = userpoolName;
  for (const nm of Object.keys(awsResources.userPoolId)) {
    if (nm.indexOf(userpoolName) != 0) continue;
    poolName = nm;
    break;
  }
  poolData.UserPoolId = awsResources.userPoolId[poolName];
  poolData.ClientId = awsResources.clientId[poolName];
};

interface IAuthInfo {
  username: string,
  idToken: string,
  refreshToken: string
}

let storageAlreadyChecked = false;
let authInfo: IAuthInfo = {username: '', idToken: '', refreshToken: ''};
let cognitoUserForResetPassword: any = null;


const checkToken = (token: string, tolerance = 100): boolean => {
  if (!token) return false;
  const tk: any = parseJwt(token);
  const timeStamp = Math.round((new Date()).getTime() / 1000);
  // console.log("REST of token time: " + (tk.exp - timeStamp));
  return tk.payload.exp > timeStamp + tolerance;
};

export const setAuthInfo = (info: { username: string, idToken: string, refreshToken: string }) => {
  authInfo = info;
  if (typeof window !== 'undefined') {
    localStorage.setItem('authInfo', JSON.stringify(authInfo));
  }
  storageAlreadyChecked = true;
};

export const clearAuthInfo = () => {
  authInfo = {username: '', idToken: '', refreshToken: ''};
  if (typeof window !== 'undefined') {
    localStorage.removeItem('authInfo');
  }

  // TIPS: cognito-identityのバグなのか、昔のログイン情報を参照してしまう場合があるので、localstorageに残ってい関連情報を全て削除する
  const removeList = Object.keys(localStorage).filter((n) => n.startsWith('CognitoIdentityServiceProvider'));
  removeList.forEach((n) => {
    localStorage.removeItem(n);
  });
};

export const getAuthInfoSimple = (): IAuthInfo|undefined => {
  if (typeof window !== 'undefined' && authInfo.username === '' && !storageAlreadyChecked) {
    const ret = localStorage.getItem('authInfo');
    if (ret) {
      authInfo = JSON.parse(ret);
      storageAlreadyChecked = true;
    }
  }
  if (authInfo.username === '' || authInfo.idToken === '') {
    return;
  }
  return authInfo;
};

export const getAuthInfo = async (update = true): Promise<{username: string, idToken: string, refreshToken: string}|undefined> => {
  const ret = getAuthInfoSimple();
  if (!ret) return;

  if (authInfo.idToken !== '' && update && !checkToken(authInfo.idToken)) {
    const res: any = await awsLoginRefresh(authInfo.username, authInfo.refreshToken);
    if (res.success) {
      setAuthInfo({username: authInfo.username, idToken: res.result.idToken, refreshToken: res.result.refreshToken});
    } else {
      clearAuthInfo();
      return;
    }
  }
  return authInfo;
};

export const setCognitoUserForResetPassword = (info: any) => {
  cognitoUserForResetPassword = info;
};

export const getCognitoUserForResetPassword = () => cognitoUserForResetPassword;

// vue-routerでverifyCodeをうまく渡せない（やり方がわからない）ので、一時的にここに保持する
let verifyCodeTemporarily = '';
export const setVerifyCodeTemporarily = (code='') => verifyCodeTemporarily = code;
export const getVerifyCodeTemporarily = () => verifyCodeTemporarily;

export const getEmail = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload.email ? token.payload.email : '';
};

export const getRole = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload['custom:role'] ? token.payload['custom:role'] : '';
};

export const getGroup = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload['custom:group'] || '';
};

export const getOrganization = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload['custom:organization'] ? token.payload['custom:organization'] : '';
};

/**
 * cognitoのグループに属している場合はその１番目のグループ名を返す
 */
export const getCognitoGroup = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload['cognito:groups'] ? token.payload['cognito:groups'][0] : '';
};

/**
 * アプリ独自の属性（文字列なら何を入れてもいい。アプリごとに判断）
 */
export const getAppAttribute = () => {
  const ret = getAuthInfoSimple();
  if (!ret) return '';
  const token = parseJwt(ret.idToken);
  if (!token.payload) return '';
  return token.payload['custom:app'] ? token.payload['custom:app'] : '';
};

export const getIssueAt = (): number => {
  const ret = getAuthInfoSimple();
  if (!ret) return 0;
  const token = parseJwt(ret.idToken);
  if (!token.payload) return 0;
  return token.payload['iat'] ? token.payload['iat'] : 0;
};

/**
 * AWSログイン機能(リフレッシュ)
 * @param userId AWS CognitoのユーザID.
 * @param refreshToken AWS CognitoのRefresh Token
 * @return Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const awsLoginRefresh = async (userId: string, refreshToken: string): Promise<{ success: boolean, code: number, message: string, result: { idToken: string, refreshToken: string } }> =>
  new Promise((resolve) => {
    if (!userId || !refreshToken) {
      resolve({
        success: false,
        code: returnCodes.auth.invalidToken.code,
        message: 'no param error in updateToken ',
        result: {idToken: '', refreshToken: ''}
      });
      return;
    }
    const rt = new CognitoRefreshToken({RefreshToken: refreshToken});
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.refreshSession(rt, (err: any, result: any) => {
      if (err) {
        setAuthInfo({username: '', idToken: '', refreshToken: ''});
        resolve({
          success: false,
          code: returnCodes.auth.loginFailed.code,
          message: 'Login Failed',
          result: {idToken: '', refreshToken: ''}
        });
        return;
      }
      const idToken = result.getIdToken().getJwtToken();
      const refreshToken = result.getRefreshToken().getToken();
      setAuthInfo({username: userId, idToken, refreshToken});
      resolve({success: true, code: returnCodes.http.success.code, message: 'OK', result: {idToken, refreshToken}});
    });
  });

/**
 * AWS Cognito ID tokenの期限切れをチェックして、必要ならRefresh Tokenでアップデートする
 * @param userId AWS CognitoのユーザID.
 * @param idToken AWS CognitoのID Token
 * @param refreshToken AWS CognitoのRefresh Token
 * @return Promise<{success: boolean, code: number, message: string, result: ({idToken: string, refreshToken: string})} ログイン結果とCognito ID Token/Refresh Token
 */
export const updateToken = async (userId: string, idToken: string, refreshToken: string): Promise<{ success: boolean, code: number, message: string, result: ({ idToken: string, refreshToken: string }) }> => {
  if (checkToken(idToken, 100)) {
    return {
      success: true, code: returnCodes.http.success.code, message: 'OK', result: {idToken, refreshToken}
    };
  }
  if (!refreshToken) {
    setAuthInfo({username: '', idToken: '', refreshToken: ''});
    return {
      success: false,
      code: returnCodes.auth.invalidToken.code,
      message: 'no param error in login ',
      result: {idToken: '', refreshToken: ''}
    };
  }
  return await awsLoginRefresh(userId, refreshToken);
};

/**
 * AWSログイン機能
 * @param userId AWS CognitoのユーザID.
 * @param password AWS CognitoのユーザIDのパスワード.
 * @return Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const login = async (userId: string, password: string): Promise<ILoginResult> =>
  new Promise((resolve) => {
    if (!userId || !password) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in login ',
        result: {}
      });
    }
    const authenticationData = {
      Username: userId,
      Password: password
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        setAuthInfo({username: userId, idToken, refreshToken});
        resolve({success: true, code: returnCodes.http.success.code, message: 'OK', result: {idToken, refreshToken}});
      },
      newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
        userAttributes;
        requiredAttributes;
        resolve({
          success: false,
          code: returnCodes.auth.passwordForceChangeRequired.code,
          message: 'password change required',
          result: cognitoUser
        });
      },
      onFailure: (err) => {
        setAuthInfo({username: '', idToken: '', refreshToken: ''});
        if (err.code === 'PasswordResetRequiredException') {
          resolve({
            success: false,
            code: returnCodes.auth.passwordRestRequired.code,
            message: 'password reset required',
            result: {}
          });
        } else if (err.code === 'UserNotConfirmedException') {
          resolve({
            success: false,
            code: returnCodes.auth.verifyRequired.code,
            message: 'verify code required',
            result: {}
          });
        } else {
          resolve({success: false, code: returnCodes.auth.loginFailed.code, message: 'failed login', result: {}});
        }
      }
    });
  });


/**
 * AWSログイン機能(リフレッシュ)
 * @param userId AWS CognitoのユーザID.
 * @param refreshToken AWS CognitoのRefresh Token
 * @return Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const loginRefresh = async (userId: string, refreshToken: string) =>
  new Promise((resolve) => {
    if (!userId || !refreshToken) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in loginRefresh',
        result: false
      });
    }
    if (!userId) {
      resolve({success: false, message: 'userId required'});
      return;
    }
    const rt = new CognitoRefreshToken({RefreshToken: refreshToken});
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.refreshSession(rt, (err, result) => {
      if (err) {
        resolve({success: false, message: 'login failed'});
        return;
      }
      const idToken = result.getIdToken().getJwtToken();
      const refreshToken = result.getRefreshToken().getToken();
      setAuthInfo({username: userId, idToken, refreshToken});
      resolve({success: true, message: {idToken, refreshToken}});
    });
  });

/**
 * AWS パスワードリセット
 * @param userId AWS CognitoのユーザID.
 * @return Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const forgetPassword = async (userId: string): Promise<{ success: boolean, code: number, message: string, result: boolean }> =>
  new Promise((resolve) => {
    if (!userId) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in forget password',
        result: false
      });
    }
    const cognitoUser = createCognitoUser(userId);
    cognitoUser.forgotPassword({
      // onSuccess: function (data) {
      onSuccess () {
        resolve({success: true, code: returnCodes.http.success.code, message: 'OK', result: true});
      },
      onFailure (err: any) {
        resolve({
          success: false,
          code: returnCodes.auth.forgotPasswordProcessFailed.code,
          message: err.message,
          result: false
        });
      }
    });
  });

/**
 * AWS パスワード設定
 * @param username AWS CognitoのユーザID.
 * @param verificationCode Verificationコード,
 * @param newPassword 新しいパスワード.
 * @return Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
// eslint-disable-next-line @typescript-eslint/require-await
export const setNewPassword = async (username: string, verificationCode: string, newPassword: string): Promise<{ success: boolean, code: number, message: string, result: boolean }> =>
  // eslint-disable-next-line no-new
  new Promise((resolve) => {
    if (!username || !verificationCode || !newPassword) {
      resolve({
        success: false,
        code: returnCodes.auth.loginFailed.code,
        message: 'no param error in set new password',
        result: false
      });
    }
    const cognitoUser = loadCognitoUser(username);
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess () {
        resolve({success: true, code: returnCodes.http.success.code, message: 'OK', result: true});
      },
      onFailure (err: any) {
        resolve({
          success: false,
          code: returnCodes.auth.resetPasswordProcessFailed.code,
          message: err.message,
          result: false
        });
      }
    });
  });

const createCognitoUser = (userId: string) => {
  const userPool = new CognitoUserPool(poolData);
  const userData: ICognitoUserData = {
    Username: userId,
    Pool: userPool
  };
  return new CognitoUser(userData);
};

const loadCognitoUser = (userId: string) => {
  const userPool = new CognitoUserPool(poolData);
  const userData: ICognitoUserData = {
    Username: userId,
    Pool: userPool
  };
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser != null) {
    cognitoUser.getSession((err: any, session: any) => {
      if (err) {
        return;
      }
      const cognitoObj: any = {};
      const key = `cognito-idp.ap-northeast-1.amazonaws.com/${poolData.UserPoolId}`;
      cognitoObj[key] = session.getIdToken().getJwtToken();
    });
  } else {
    return new CognitoUser(userData);
  }
  return cognitoUser;
};

export const getUserId = (): string => {
  const userPool = new CognitoUserPool(poolData);
  const cognitoUser: any = userPool.getCurrentUser();
  return cognitoUser.username;
};


/**
 * AWSサインアップ機能
 * @param username AWS CognitoのユーザIDとなるメールアドレス
 * @param password AWS CognitoのユーザIDのパスワード.
 * @return Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token
 */
export const signup = async (username: string, password: string): Promise<{ success: boolean, message: string }> =>
  new Promise((resolve) => {
    if (!username || !password) {
      resolve({success: false, message: 'no param error'});
    }
    const userPool = new CognitoUserPool(poolData);
    const attributeList: CognitoUserAttribute[] = [];

    const datausername = {
      Name: 'username',
      Value: username
    };
    const attributeUsername: CognitoUserAttribute = new CognitoUserAttribute(datausername);
    attributeList.push(attributeUsername);
    userPool.signUp(username, password, attributeList, [], (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        resolve({success: false, message: 'failed to signUp'});
      }
      resolve({success: true, message: ''});
    });
  });

/**
 * AWS codeの検証
 * @param username AWS CognitoのユーザIDのメールアドレス.
 * @param code 検証コード.
 * @return Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const verify = async (username: string, code: string): Promise<{ success: boolean, message: string }> =>
  new Promise((resolve) => {
    if (!username) {
      resolve({success: false, message: 'no param error'});
    }
    const cognitoUser = createCognitoUser(username);
    // cognitoUser.confirmRegistration(code, true, function (err, result) {
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        resolve({success: false, message: 'the verification code is invalid'});
      }
      // console.log('call result: ' + result);
      resolve({success: true, message: ''});
    });
  });

/**
 * AWS パスワード設定
 * @param username AWS CognitoのユーザIDとなるメールアドレス
 * @param oldPassword 現在のパスワード
 * @param newPassword 新しいパスワード
 * @return Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const changePassword = async (username: string, oldPassword: string, newPassword: string): Promise<{ success: boolean, message: string }> =>
  new Promise((resolve) => {
    if (!username || !oldPassword || !newPassword) {
      resolve({success: false, message: 'no param error'});
    }
    const cognitoUser = loadCognitoUser(username);
    // cognitoUser.changePassword(oldPassword, newPassword, function(err, result) {
    cognitoUser.changePassword(oldPassword, newPassword, (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        // return false;
        resolve({success: false, message: err.message});
      }
      // console.log('call result: ' + result);
      // return true;
      resolve({success: true, message: 'OK'});
    });
  });

export const awsDelete = (username: string) => {
  const cognitoUser = loadCognitoUser(username);
  cognitoUser.deleteUser((err) => {
    if (err) {
      // alert(err.message || JSON.stringify(err));
      return;
    }
    alert('ご利用いただきありがとうございました');
  });
};
